import {
  faKey,
  faUser,
  faEye,
  faEyeSlash,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { useEffect, useState } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { GET_MICROSOFT_LOGIN_URL } from 'services/api/auth';

const MICROSOFT_AUTH_HOSTS = [
  'login.microsoftonline.com',
  'login.microsoftonline.us',
  'login.microsoftonline.de',
  'login.partner.microsoftonline.cn',
  'login.microsoft.com'
];

const isAllowedMicrosoftAuthUrl = (candidate: string): boolean => {
  if (!candidate || typeof candidate !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'https:') {
      return false;
    }

    const host = parsed.hostname.toLowerCase();
    return MICROSOFT_AUTH_HOSTS.some(allowedHost => {
      const allowed = allowedHost.toLowerCase();
      return host === allowed || host.endsWith(`.${allowed}`);
    });
  } catch {
    return false;
  }
};

const loginSchema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
});
type LoginFormData = yup.InferType<typeof loginSchema>;

const SignInForm = ({
  layout,
  email,
  setEmail,
  password,
  setPassword,
  handleLogin,
  errorMessage,
  isLoading
}: {
  layout: 'simple' | 'card' | 'split';
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  password: string;
  setPassword: React.Dispatch<React.SetStateAction<string>>;
  handleLogin: (data: LoginFormData) => void;
  errorMessage: string | null;
  isLoading: boolean;
}) => {
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState<boolean>(false);
  const [microsoftError, setMicrosoftError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: email,
      password: password
    }
  });

  const onSubmit = (data: LoginFormData) => {
    setEmail(data.email);
    setPassword(data.password);
    handleLogin(data);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleMicrosoftLogin = async () => {
    try {
      setIsMicrosoftLoading(true);
      const response = await GET_MICROSOFT_LOGIN_URL();
      const authUrl = response?.data?.data?.authUrl;

      if (!authUrl || authUrl === 'undefined') {
        console.error('No authUrl found in response');
        setMicrosoftError('Unable to start Microsoft login. Please try again.');
        setShowErrorMessage(true);
        return;
      }

      if (!isAllowedMicrosoftAuthUrl(authUrl)) {
        console.error('Blocked unexpected Microsoft auth URL:', authUrl);
        setMicrosoftError('The login link looked suspicious and was blocked.');
        setShowErrorMessage(true);
        return;
      }

      localStorage.setItem('microsoftAuthInProgress', 'true');
      window.location.replace(authUrl);
    } catch (error: any) {
      console.error('Microsoft login error:', error?.message || error);
      setMicrosoftError(
        'We could not reach Microsoft right now. Please try again shortly.'
      );
      setShowErrorMessage(true);
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      setMicrosoftError(errorMessage);
      setShowErrorMessage(true);
    }
  }, [errorMessage]);

  return (
    <>
      <div className="text-center mb-7" title={layout}>
        <h3 className="text-1000">Sign In</h3>
        <p className="text-700">Get access to your account</p>
      </div>

      {/* Microsoft Login Button */}
      <Button
        variant="outline-primary"
        className="w-100 mb-4"
        onClick={handleMicrosoftLogin}
        disabled={isMicrosoftLoading}
      >
        <FontAwesomeIcon icon={faBuilding} className="me-2" />
        {isMicrosoftLoading ? 'Connecting...' : 'Sign in with Microsoft'}
      </Button>

      <div className="position-relative">
        <hr className="bg-200 mt-5 mb-4" />
        <div className="divider-content-center">Or use Email or Username</div>
      </div>

      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email">Email address</Form.Label>
          <div className="form-icon-container">
            <Form.Control
              id="email"
              type="email"
              className={`form-icon-input ${errors.email ? 'is-invalid' : ''}`}
              placeholder="name@example.com"
              {...register('email')}
            />
            {errors.email && (
              <Form.Control.Feedback type="invalid">
                {errors.email.message}
              </Form.Control.Feedback>
            )}
            <FontAwesomeIcon
              icon={faUser}
              className="text-900 fs-9 form-icon"
            />
          </div>
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="password">Password</Form.Label>
          <div className="form-icon-container position-relative">
            <Form.Control
              id="password"
              type={showPassword ? 'text' : 'password'}
              className={`form-icon-input ${
                errors.password ? 'is-invalid' : ''
              }`}
              placeholder="Password"
              {...register('password')}
            />
            {errors.password && (
              <Form.Control.Feedback type="invalid">
                {errors.password.message}
              </Form.Control.Feedback>
            )}
            <FontAwesomeIcon icon={faKey} className="text-900 fs-9 form-icon" />
            <button
              type="button"
              className="btn btn-link position-absolute end-0 top-0 mt-2 me-2 text-decoration-none p-0 border-0 bg-transparent"
              onClick={togglePasswordVisibility}
              tabIndex={-1}
              style={{ zIndex: 5 }}
            >
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
                className="text-700"
              />
            </button>
          </div>
        </Form.Group>

        <Row className="flex-between-center mb-7">
          <Col xs="auto">
            <Form.Check type="checkbox" className="mb-0">
              <Form.Check.Input
                type="checkbox"
                name="remember-me"
                id="remember-me"
                defaultChecked
              />
              <Form.Check.Label htmlFor="remember-me" className="mb-0">
                Remember me
              </Form.Check.Label>
            </Form.Check>
          </Col>
          <Col xs="auto">
            <Link
              to={`/auth/forgot-password?email=${email}`}
              className="fs-9 fw-semi-bold"
            >
              Forgot Password?
            </Link>
          </Col>
        </Row>

        <Button
          type="submit"
          variant="primary"
          className="w-100 mb-3"
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>
      </Form>

      {showErrorMessage && (
        <Alert
          variant="danger"
          className="text-white"
          onClose={() => {
            setShowErrorMessage(false);
            setMicrosoftError(null);
          }}
          dismissible
        >
          <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
          <p>{microsoftError || errorMessage}</p>
        </Alert>
      )}
    </>
  );
};

export default SignInForm;
